/* eslint-disable react-hooks/exhaustive-deps */
import { Actions, FSpread } from '@client/new/helpers/types';
import { useUpdate } from '@client/new/helpers/useUpdate';
import _ from 'lodash';
import { Folder, minFileSizeWarning, Video } from '../models/videos.domain';
import {
  toListItem,
  getFooterButtons,
  ButtonVariant,
  toFolderItem,
} from '../components/addFromLibrary/mappers';
import {
  UploadToLibraryModel,
  useUploadToLibraryModel,
} from '../models/uploadToLibrary.model';
import { useEffect, useMemo } from 'react';
import { ButtonColor } from '@design-system/components/button/button.css';
import { nameMatches } from './mappers';
import { VideoById } from '@client/new/common/videos/useUpload';
import {
  contentFilter,
  ContentFilters,
  getFoldersChildrenCount,
  getFoldersScope,
  getVideosScope,
  sortList,
} from '@client/new/common/library/helpers';
import { FolderListItem } from '../components/addFromLibrary/libraryList';
import { ApolloError } from '@apollo/client';

export type SelectTab = 'Library' | 'Upload';
const tabs: Array<SelectTab> = ['Library', 'Upload'];
export type Modal = ContentFilters | 'None';

type PayloadMap = {
  close: never;
  search: string;
  refetchVideos: never;
  selectTab: SelectTab;
  selectVideo: string;
  addVideo: never;
  onDrop: { file: File; videoType: ContentFilters | null };
  showSelectThumbnail: never;
  showEditName: never;
  upload: never;
  closeConfirmation: never;
  cancel: never;
  confirmCancel: never;
  editName: string;
  thumbnailTimestamp: number;
  cancelUpload: never;
  openFolder: number | null;
  filterVideos: ContentFilters;
  toggleViewSize: null;
};
export type VideosVMAction = Actions<PayloadMap>;
export type AddFromLibraryVMDispatcher = FSpread<PayloadMap>;
export type UploadStep = 'Name' | 'Thumbnail' | 'Uploading' | 'Processing';

type VideosVMState = {
  searchText: string;
  activeTab: SelectTab;
  selectedIds: string[] | null;
  uploadStep: UploadStep;
  confirmModal: boolean;
  currentFolder: null | Folder;
  filterVideos: ContentFilters | null;
  smallerView: boolean;
};

const initialState: VideosVMState = {
  searchText: '',
  activeTab: 'Library',
  selectedIds: null,
  uploadStep: 'Name',
  confirmModal: false,
  currentFolder: null,
  filterVideos: null,
  smallerView: false,
};

export type VideoListItem = {
  id: string;
  imageUrl: string;
  title: string;
  duration: string;
  tag: {
    label: string;
  };
  date: string;
  onClick: () => void;
};

export type AddFromLibaryVM = Pick<VideosVMState, 'activeTab'> & {
  dispatch: AddFromLibraryVMDispatcher;
  library: {
    folders: Array<FolderListItem>;
    videos: Array<VideoListItem>;
    currentFolder: (Folder & { count: string }) | null;
    selectedIds: VideosVMState['selectedIds'];
    searchText: VideosVMState['searchText'];
    actionsDisabled: boolean;
    activeVideoFilter: ContentFilters | null;
    filterVideos: (type: ContentFilters) => void;
    backFromCurrentFolder: () => void;
    selectVideo: (id: string) => void;
    search: (value: string) => void;
    fetchError: boolean;
  };
  upload: {
    showDropZone: boolean;
    showPreview: boolean;
    file: UploadToLibraryModel['uploadFile'];
    step: VideosVMState['uploadStep'];
    thumbnailTimestamp: UploadToLibraryModel['thumbnailTimestamp'];
    progress: number;
    isUploading: boolean;
    uploadSizeWarning?: boolean;
    validationError: UploadToLibraryModel['validationError'];
  };
  confirmationModal: { open: boolean };
  footerButtons: Array<{
    props: {
      color: ButtonColor;
      variant: ButtonVariant;
      onClick: () => void;
      disabled?: boolean;
    };
    label: string;
  }>;
  tabs: Array<'Library' | 'Upload'>;
  videoType: ContentFilters | null;
  smallerView: boolean;
};

export const useAddFromLibraryVM = (p: {
  closeModal: () => void;
  influencerId: number;
  library: {
    videos: Array<Video>;
    folders: Array<Folder>;
    error: ApolloError | undefined;
    loading: boolean;
    refreshLibrary: () => void;
  };
  type: ContentFilters | null;
  action: 'add' | 'update';
  addVideo: (video: VideoById) => void;
  addVideoFromSelection: (ids: string[]) => void;
}): AddFromLibaryVM => {
  const [state, updateState] = useUpdate(initialState);
  const model = useUploadToLibraryModel(p);

  useEffect(() => {
    updateState({ filterVideos: p.type });
  }, []);

  useEffect(() => {
    if (state.uploadStep !== 'Uploading' && model.isUploading) {
      updateState({ uploadStep: 'Uploading' });
    }
  }, [model.isUploading, state.uploadStep]);

  useEffect(() => {
    if (state.uploadStep !== 'Processing' && model.isProcessing) {
      updateState({ uploadStep: 'Processing' });
    }
  }, [model.isProcessing, state.uploadStep]);

  useEffect(() => {
    if (model.isUploadFinished) {
      updateState(initialState);
    }
  }, [model.isUploadFinished]);

  const dispatch: AddFromLibraryVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as VideosVMAction;
    switch (action.type) {
      case 'close': {
        if (!model.uploadFile) {
          return updateState(initialState);
        }
        return updateState({ confirmModal: true });
      }
      case 'search':
        return updateState({ searchText: action.payload });
      case 'selectTab':
        return updateState({ activeTab: action.payload });
      case 'selectVideo':
        selectVideo(action.payload);
        return null;
      case 'addVideo': {
        if (state.selectedIds) {
          p.addVideoFromSelection(state.selectedIds);
          updateState(initialState);
        }
        return null;
      }
      case 'showSelectThumbnail':
        return updateState({ uploadStep: 'Thumbnail' });
      case 'showEditName':
        return updateState({ uploadStep: 'Name' });
      case 'closeConfirmation':
        return updateState({ confirmModal: false });
      case 'cancel': {
        if (!model.uploadFile) {
          return updateState(initialState);
        }
        return updateState({ confirmModal: true });
      }
      case 'cancelUpload':
        model.dispatch('cancel');
        return updateState(initialState);
      case 'confirmCancel': {
        model.dispatch('cancel');
        return updateState(initialState);
      }
      case 'refetchVideos':
      case 'upload':
        model.dispatch(action.type);
        return null;
      case 'onDrop':
      case 'editName':
      case 'thumbnailTimestamp':
        model.dispatch(action.type, action.payload);
        return null;
      case 'openFolder':
        return updateState({
          currentFolder:
            action.payload === null
              ? null
              : p.library.folders.find((f) => f.id === action.payload),
        });
      case 'filterVideos':
        return updateState({
          filterVideos:
            action.payload === state.filterVideos ? null : action.payload,
        });
      case 'toggleViewSize':
        return updateState((prev) => ({
          ...prev,
          smallerView: !prev.smallerView,
        }));
    }
  };

  const selectVideo = (id: string) => {
    if (p.type && p.action === 'update') {
      updateState({ selectedIds: [id] });
      return;
    }
    const mappedIds = !state.selectedIds?.includes(id)
      ? [...(state.selectedIds || []), id]
      : state.selectedIds.filter((i) => i !== id);

    updateState({ selectedIds: mappedIds });
  };

  const filteredVideos = useMemo(
    () =>
      p.library.videos
        .filter((v) => contentFilter(v, state.filterVideos) && !v.isProcessing)
        .filter(nameMatches(state.searchText))
        .sort((a, b) => sortList(a, b, 'updatedAt', false)),
    [p.library.videos, state.filterVideos, state.searchText],
  );

  const filteredFolders = useMemo(
    () =>
      p.library.folders
        .filter(nameMatches(state.searchText))
        .sort((a, b) => sortList(a, b, 'updatedAt', false)),
    [p.library.folders, state.searchText],
  );

  const scopedVideos = getVideosScope(
    filteredVideos,
    state.currentFolder?.id || null,
    state.searchText,
  );

  const scopedFolders = getFoldersScope(
    filteredFolders,
    state.currentFolder?.id || null,
    state.searchText,
  );

  const videos = scopedVideos.map((v) =>
    toListItem(v, () => dispatch('selectVideo', v.id)),
  );

  const folders = scopedFolders.map((item) =>
    toFolderItem({
      item,
      ...p.library,
      onClick: () => dispatch('openFolder', item.id),
    }),
  );

  const actionsDisabled = _.isEmpty(videos);

  return {
    activeTab: state.activeTab,
    dispatch,
    tabs,
    library: {
      videos,
      folders: folders,
      currentFolder: !state.currentFolder
        ? null
        : {
            ...state.currentFolder,
            count: getFoldersChildrenCount(
              p.library.folders,
              p.library.videos,
              state.currentFolder.id,
            ),
          },
      selectedIds: state.selectedIds,
      searchText: state.searchText,
      actionsDisabled,
      activeVideoFilter: state.filterVideos,
      filterVideos: (type: ContentFilters) => dispatch('filterVideos', type),
      backFromCurrentFolder: () =>
        dispatch('openFolder', state.currentFolder?.parentId || null),
      selectVideo: (id: string) => dispatch('selectVideo', id),
      search: (value: string) => dispatch('search', value),
      fetchError: !!p.library.error,
    },
    upload: {
      showDropZone: state.activeTab === 'Upload' && !model.uploadFile,
      showPreview: state.activeTab === 'Upload' && model.uploadFile !== null,
      file: model.uploadFile,
      step: state.uploadStep,
      thumbnailTimestamp: model.thumbnailTimestamp,
      progress: model.progress,
      isUploading: model.isUploading,
      uploadSizeWarning: !!(
        model.uploadFile && model.uploadFile.size > minFileSizeWarning
      ),
      validationError: model.validationError,
    },
    confirmationModal: {
      open: state.confirmModal,
    },
    footerButtons: getFooterButtons({
      disabled: !!model.validationError,
      activeTab: state.activeTab,
      dispatch,
      selectedIds: state.selectedIds,
      actionsDisabled,
      uploadStep: state.uploadStep,
      inProgress:
        state.uploadStep === 'Uploading' || state.uploadStep === 'Processing',
      closeModal: p.closeModal,
    }),
    videoType: p.type,
    smallerView: state.smallerView,
  };
};
